/**
 * Extracts the dominant color from an image URL using canvas sampling
 * @param {string} imageUrl - The URL of the image to analyze
 * @param {number} quality - Sampling quality (1-10, lower = faster but less accurate)
 * @returns {Promise<string>} - RGB color string like "rgb(r, g, b)"
 */
export const getDominantColor = (imageUrl, quality = 5) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        // Use smaller canvas for performance
        const scaleFactor = 0.25;
        canvas.width = img.width * scaleFactor;
        canvas.height = img.height * scaleFactor;
        
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        const pixelCount = pixels.length / 4;
        
        // Color buckets for averaging
        let r = 0, g = 0, b = 0;
        let count = 0;
        
        // Sample pixels with quality setting (skip pixels for performance)
        for (let i = 0; i < pixelCount; i += quality) {
          const offset = i * 4;
          const red = pixels[offset];
          const green = pixels[offset + 1];
          const blue = pixels[offset + 2];
          const alpha = pixels[offset + 3];
          
          // Skip transparent pixels and very bright/dark pixels (likely background)
          if (alpha > 125) {
            const brightness = (red + green + blue) / 3;
            // Skip very bright (>240) or very dark (<15) pixels
            if (brightness > 15 && brightness < 240) {
              r += red;
              g += green;
              b += blue;
              count++;
            }
          }
        }
        
        if (count === 0) {
          // Fallback if no valid pixels found
          resolve('rgb(223, 223, 223)');
          return;
        }
        
        // Calculate average
        r = Math.round(r / count);
        g = Math.round(g / count);
        b = Math.round(b / count);
        
        // Lighten the color for better background appearance
        const lightenFactor = 1.4;
        r = Math.min(255, Math.round(r * lightenFactor));
        g = Math.min(255, Math.round(g * lightenFactor));
        b = Math.min(255, Math.round(b * lightenFactor));
        
        resolve(`rgb(${r}, ${g}, ${b})`);
      } catch (error) {
        console.error('Error extracting color:', error);
        resolve('rgb(223, 223, 223)'); // Fallback to default grey
      }
    };
    
    img.onerror = () => {
      console.error('Error loading image for color extraction');
      resolve('rgb(223, 223, 223)'); // Fallback to default grey
    };
    
    img.src = imageUrl;
  });
};

/**
 * Cache for storing dominant colors to avoid re-computation
 */
const colorCache = new Map();

/**
 * Gets dominant color with caching
 * @param {string} imageUrl - The URL of the image
 * @param {number} quality - Sampling quality
 * @returns {Promise<string>} - RGB color string
 */
export const getCachedDominantColor = async (imageUrl, quality = 5) => {
  if (colorCache.has(imageUrl)) {
    return colorCache.get(imageUrl);
  }
  
  const color = await getDominantColor(imageUrl, quality);
  colorCache.set(imageUrl, color);
  return color;
};
